/**
 * Reverse-chronological activity feed.
 *
 * Server pages come from GET /api/events (limit/skip); live events arrive on
 * the "ap:event" socket and are prepended, de-duplicated by _id. Paging uses a
 * separate fetched-count rather than the rendered list length, so live arrivals
 * never shift the server-side window.
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getEvents, toMessage } from "../lib/api";
import { SOCKET_EVENTS, useSocketConnected, useSocketEvent } from "../lib/socket";
import type { EventSeverity, EventType, WatchEvent } from "../lib/types";
import EventRow, { EVENT_TYPES, eventLabel, formatDay } from "../components/EventRow";

type Phase = "loading" | "ready" | "error";

interface Query {
  type: EventType | "all";
  severity: EventSeverity | "all";
}

const LIMIT = 40;
const SEVERITIES: EventSeverity[] = ["alert", "notable", "info"];

function matchesQuery(event: WatchEvent, query: Query): boolean {
  if (query.type !== "all" && event.type !== query.type) return false;
  if (query.severity !== "all" && event.severity !== query.severity) return false;
  return true;
}

function mergeUnique(existing: WatchEvent[], incoming: WatchEvent[]): WatchEvent[] {
  const ids = new Set(existing.map((e) => e._id));
  const additions = incoming.filter((e) => !ids.has(e._id));
  return additions.length === 0 ? existing : [...existing, ...additions];
}

const CSS = `
.awfeed{
  --ink:#0D0D0D;--red:#FF0000;--pink:#E89AC7;--muted:#8A8A8A;--line:rgba(13,13,13,.14);
  max-width:900px;margin:0 auto;padding:32px 20px 96px;
  font-family:"Sora","Helvetica Neue",Arial,sans-serif;color:var(--ink);
}
.awfeed-top{display:flex;align-items:flex-end;justify-content:space-between;gap:16px;flex-wrap:wrap}
.awfeed-title{
  margin:0;
  font-family:"Anton","Arial Narrow","Helvetica Neue",Impact,sans-serif;
  font-size:clamp(30px,6vw,46px);line-height:.95;letter-spacing:.01em;text-transform:uppercase;
}
.awfeed-sub{margin:8px 0 0;font-size:11.5px;line-height:1.6;color:var(--muted);max-width:46ch}
.awfeed-live{
  display:inline-flex;align-items:center;gap:7px;
  font-family:"Oswald","Arial Narrow",sans-serif;
  font-size:10px;letter-spacing:.16em;text-transform:uppercase;color:var(--muted);
  border:1px solid var(--line);padding:5px 9px;white-space:nowrap;
}
.awfeed-live.is-live{color:var(--ink);border-color:var(--ink)}
.awfeed-dot{width:7px;height:7px;border-radius:50%;background:#CFCFCF;display:block}
.awfeed-live.is-live .awfeed-dot{background:var(--pink)}
.awfeed-live.is-pulse .awfeed-dot{animation:awfeed-pulse 1.1s ease-out 1}
@keyframes awfeed-pulse{
  0%{transform:scale(1);box-shadow:0 0 0 0 rgba(232,154,199,.9)}
  100%{transform:scale(1);box-shadow:0 0 0 9px rgba(232,154,199,0)}
}

.awfeed-filters{margin:26px 0 0;border-top:1px solid var(--line);padding-top:16px}
.awfeed-group{display:flex;align-items:baseline;gap:12px;flex-wrap:wrap;margin-bottom:10px}
.awfeed-legend{
  font-family:"Oswald","Arial Narrow",sans-serif;
  font-size:10px;letter-spacing:.16em;text-transform:uppercase;color:var(--muted);
  flex:0 0 62px;
}
.awfeed-chips{display:flex;flex-wrap:wrap;gap:6px;flex:1 1 auto;min-width:0}
.awfeed-chip{
  font-family:"Oswald","Arial Narrow",sans-serif;
  font-size:10px;letter-spacing:.12em;text-transform:uppercase;line-height:1;
  padding:6px 8px;border:1px solid #CFCFCF;background:transparent;color:var(--muted);
  cursor:pointer;white-space:nowrap;
}
.awfeed-chip:hover{border-color:var(--ink);color:var(--ink)}
.awfeed-chip.is-on{background:var(--ink);border-color:var(--ink);color:#FFFFFF}
.awfeed-chip.is-alert.is-on{background:var(--red);border-color:var(--red)}

.awfeed-count{
  margin:14px 0 0;font-size:10.5px;letter-spacing:.06em;text-transform:uppercase;color:var(--muted);
}
.awfeed-list{list-style:none;margin:16px 0 0;padding:0;border:1px solid var(--line);background:#FFFFFF}
.awfeed-day{
  list-style:none;padding:11px 16px;background:#F6F6F6;border-bottom:1px solid var(--line);
  font-family:"Oswald","Arial Narrow",sans-serif;
  font-size:10px;letter-spacing:.18em;text-transform:uppercase;color:var(--muted);
  position:sticky;top:0;z-index:1;
}
.awfeed-note{
  border:1px solid var(--line);background:#FFFFFF;padding:44px 24px;text-align:center;margin-top:16px;
}
.awfeed-note h2{
  margin:0;
  font-family:"Anton","Arial Narrow","Helvetica Neue",Impact,sans-serif;
  font-size:20px;letter-spacing:.02em;text-transform:uppercase;
}
.awfeed-note p{margin:10px auto 0;font-size:12px;line-height:1.7;color:var(--muted);max-width:42ch}
.awfeed-swirl{display:block;margin:0 auto 14px;width:96px;height:26px}
.awfeed-actions{display:flex;justify-content:center;margin-top:20px}
.awfeed-btn{
  font-family:"Oswald","Arial Narrow",sans-serif;
  font-size:11px;letter-spacing:.18em;text-transform:uppercase;
  padding:12px 26px;border:1px solid var(--ink);background:transparent;color:var(--ink);cursor:pointer;
}
.awfeed-btn:hover:not(:disabled){background:var(--ink);color:#FFFFFF}
.awfeed-btn:disabled{opacity:.45;cursor:default}
.awfeed-skeleton{
  border:1px solid var(--line);background:#FFFFFF;margin-top:16px;
}
.awfeed-skeleton span{
  display:block;height:56px;border-bottom:1px solid var(--line);
  background:linear-gradient(90deg,#FFFFFF 0%,#F3F3F3 50%,#FFFFFF 100%);
  background-size:220% 100%;animation:awfeed-shimmer 1.4s linear infinite;
}
.awfeed-skeleton span:last-child{border-bottom:none}
@keyframes awfeed-shimmer{from{background-position:120% 0}to{background-position:-120% 0}}
.awfeed-error{color:#8C1D1D}

@media (max-width:640px){
  .awfeed{padding:24px 14px 80px}
  .awfeed-legend{flex-basis:100%}
}
`;

const STYLE_ID = "aw-feed-styles";
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const tag = document.createElement("style");
  tag.id = STYLE_ID;
  tag.textContent = CSS;
  document.head.appendChild(tag);
}

function Swirl() {
  return (
    <svg className="awfeed-swirl" viewBox="0 0 120 34" aria-hidden="true" focusable="false">
      <path
        d="M4 24c8-14 16-18 22-10 5 7-6 16-10 10-5-8 10-20 22-20 10 0 12 14 20 14 7 0 9-8 16-8 6 0 8 6 14 6 5 0 8-3 12-8"
        fill="none"
        stroke="#E89AC7"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function Feed() {
  const [query, setQuery] = useState<Query>({ type: "all", severity: "all" });
  const [events, setEvents] = useState<WatchEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [fetchedCount, setFetchedCount] = useState(0);
  const [phase, setPhase] = useState<Phase>("loading");
  const [error, setError] = useState("");
  const [moreBusy, setMoreBusy] = useState(false);
  const [pulse, setPulse] = useState(0);

  const connected = useSocketConnected();

  // Ids already on screen. Kept in a ref so the socket handler can reject
  // duplicates without running side effects inside a state updater.
  const idsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    idsRef.current = new Set(events.map((e) => e._id));
  }, [events]);

  const filters = useMemo(
    () => ({
      type: query.type === "all" ? undefined : query.type,
      severity: query.severity === "all" ? undefined : query.severity,
    }),
    [query],
  );

  useEffect(() => {
    const controller = new AbortController();
    setPhase("loading");
    setError("");

    getEvents({ ...filters, limit: LIMIT, skip: 0 }, controller.signal)
      .then((page) => {
        setEvents(page.events);
        setFetchedCount(page.events.length);
        setTotal(page.total);
        setPhase("ready");
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(toMessage(err));
        setPhase("error");
      });

    return () => controller.abort();
  }, [filters]);

  const loadMore = useCallback(async () => {
    setMoreBusy(true);
    try {
      const page = await getEvents({ ...filters, limit: LIMIT, skip: fetchedCount });
      setEvents((prev) => mergeUnique(prev, page.events));
      setFetchedCount((count) => count + page.events.length);
      setTotal(page.total);
      setError("");
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setMoreBusy(false);
    }
  }, [filters, fetchedCount]);

  // useSocketEvent reads the handler through a ref, so this closure always sees
  // the current filters without resubscribing.
  useSocketEvent<WatchEvent>(SOCKET_EVENTS.event, (incoming) => {
    if (!incoming?._id) return;
    if (!matchesQuery(incoming, query)) return;
    if (idsRef.current.has(incoming._id)) return;
    idsRef.current.add(incoming._id);
    setEvents((prev) => (prev.some((e) => e._id === incoming._id) ? prev : [incoming, ...prev]));
    setTotal((t) => t + 1);
    setPulse((p) => p + 1);
  });

  const grouped = useMemo(() => {
    const out: Array<{ day: string; items: WatchEvent[] }> = [];
    for (const event of events) {
      const day = formatDay(event.occurredAt);
      const last = out[out.length - 1];
      if (last && last.day === day) last.items.push(event);
      else out.push({ day, items: [event] });
    }
    return out;
  }, [events]);

  const hasMore = fetchedCount > 0 && events.length < total;
  const isFiltered = query.type !== "all" || query.severity !== "all";

  return (
    <section className="awfeed">
      <header className="awfeed-top">
        <div>
          <h1 className="awfeed-title">Activity</h1>
          <p className="awfeed-sub">
            Every drop, restock, price move and quiet edit the watcher has caught,
            newest first.
          </p>
        </div>
        <span
          key={pulse}
          className={`awfeed-live${connected ? " is-live" : ""}${pulse > 0 ? " is-pulse" : ""}`}
        >
          <span className="awfeed-dot" aria-hidden="true" />
          {connected ? "Live" : "Offline"}
        </span>
      </header>

      <div className="awfeed-filters">
        <div className="awfeed-group">
          <span className="awfeed-legend">Severity</span>
          <div className="awfeed-chips">
            <button
              type="button"
              className={`awfeed-chip${query.severity === "all" ? " is-on" : ""}`}
              onClick={() => setQuery((q) => ({ ...q, severity: "all" }))}
            >
              All
            </button>
            {SEVERITIES.map((severity) => (
              <button
                key={severity}
                type="button"
                className={`awfeed-chip is-${severity}${query.severity === severity ? " is-on" : ""}`}
                onClick={() => setQuery((q) => ({ ...q, severity }))}
              >
                {severity}
              </button>
            ))}
          </div>
        </div>

        <div className="awfeed-group">
          <span className="awfeed-legend">Type</span>
          <div className="awfeed-chips">
            <button
              type="button"
              className={`awfeed-chip${query.type === "all" ? " is-on" : ""}`}
              onClick={() => setQuery((q) => ({ ...q, type: "all" }))}
            >
              All
            </button>
            {EVENT_TYPES.map((type) => (
              <button
                key={type}
                type="button"
                className={`awfeed-chip${query.type === type ? " is-on" : ""}`}
                onClick={() => setQuery((q) => ({ ...q, type }))}
              >
                {eventLabel(type)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {phase === "loading" ? (
        <div className="awfeed-skeleton" aria-busy="true" aria-label="Loading activity">
          <span />
          <span />
          <span />
          <span />
          <span />
        </div>
      ) : null}

      {phase === "error" ? (
        <div className="awfeed-note">
          <h2>Feed unreachable</h2>
          <p className="awfeed-error">{error}</p>
          <div className="awfeed-actions">
            <button type="button" className="awfeed-btn" onClick={() => setQuery((q) => ({ ...q }))}>
              Try again
            </button>
          </div>
        </div>
      ) : null}

      {phase === "ready" && events.length === 0 ? (
        <div className="awfeed-note">
          <Swirl />
          <h2>Nothing has moved</h2>
          <p>
            {isFiltered
              ? "No events match this filter yet. The watcher is still running — widen the filter and check back."
              : "The watcher is up and the store is quiet. New drops, restocks and price moves land here the moment they happen."}
          </p>
        </div>
      ) : null}

      {phase === "ready" && events.length > 0 ? (
        <>
          <p className="awfeed-count">
            Showing {events.length} of {total} event{total === 1 ? "" : "s"}
            {isFiltered ? " (filtered)" : ""}
          </p>

          <ul className="awfeed-list">
            {grouped.map((group) => (
              <Fragment key={`${group.day}-${group.items[0]._id}`}>
                <li className="awfeed-day">{group.day}</li>
                {group.items.map((event) => (
                  <EventRow key={event._id} event={event} />
                ))}
              </Fragment>
            ))}
          </ul>

          {error ? <p className="awfeed-count awfeed-error">{error}</p> : null}

          {hasMore ? (
            <div className="awfeed-actions">
              <button type="button" className="awfeed-btn" onClick={loadMore} disabled={moreBusy}>
                {moreBusy ? "Loading…" : "Load more"}
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

export default Feed;
